import type { OnInit } from '@angular/core';
import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, Renderer2 } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import type { AppSelectOption } from '@shared/form-fields/options/select-option';
import { BaseSelectFormFieldComponent } from '@shared/form-fields/select/base-select-form-field.component';
import { ApiParams } from '@shared/http/api-params';
import { CarerContractFetcher } from '@shared/modules/carer-contracts/carer-contract.fetcher';
import { CarerContractPresenter } from '@shared/modules/carer-contracts/carer-contract.presenter';
import { isPresent } from '@shared/utils/helpers';
import { orderBy } from 'lodash-es';
import type { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import { BehaviorSubject, combineLatest, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators';
import type { CarerContract } from '../carer-contract';

@Component({
    selector: 'app-carer-contract-select',
    styleUrls: ['./carer-contract-select.component.scss'],
    templateUrl: '../../../form-fields/select/select-form-field.component.html',
    providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CarerContractSelectComponent), multi: true }],
})
@UntilDestroy()
export class CarerContractSelectComponent extends BaseSelectFormFieldComponent<CarerContract> implements OnInit {
    private _requestedApiParams: ApiParams = {};
    private readonly _active$ = new BehaviorSubject<boolean | DateTime>(null);
    private readonly _carerId$ = new BehaviorSubject<string>(null);
    private readonly _apiParams$ = new BehaviorSubject<ApiParams>(null);
    @Output() userUnauthorised = new EventEmitter();

    @Input()
    set carerId(value: string) {
        this._carerId$.next(value);
    }

    @Input() set apiParams(value: ApiParams) {
        this._requestedApiParams = value;
        this._buildApiParams();
    }

    @Input() setDefault = false;
    @Input() defaultContractTypePriorities: string[];

    @Input() set active(value: true | DateTime) {
        this._active$.next(value);
    }

    private readonly contracts$: Observable<CarerContract[]> = combineLatest([this._carerId$, this._apiParams$]).pipe(
        distinctUntilChanged(),
        filter(([carerId]) => isPresent(carerId)),
        switchMap(([carerId, params]) => this.fetcher.all({
            ...params,
            carer_id: carerId
        })),
        map(response => CarerContractPresenter.attachCollection(response.data)),
        // For situations (On Call users) where the default contract should be set if unauthorised
        catchError((error, caught) => {
            if (error?.status === 403) {
                this.userUnauthorised.emit(true);
                this.disabled = true;
                return of([]);
            }

            return caught;
        }),
        shareReplay(1)
    );

    private readonly apiOptions$ = combineLatest([this.contracts$, this._active$]).pipe(
        filter(([contracts]) => isPresent(contracts)),
        map(([contracts, active]) => contracts.map(contract => {
            // Check if active
            let disabled = false;
            let description = `${contract.presenter.dateRange}`;

            if (active) {
                disabled = !contract.presenter.isActive(active);
            }

            if (disabled) {
                description += ' • Inactive';
            }

            return {
                label: contract.name,
                value: contract,
                description,
                disabled
            };
        })),
        map(options => orderBy(options, ['disabled', 'label'], ['asc', 'asc']))
    );

    constructor(element: ElementRef,
        renderer: Renderer2,
        private fetcher: CarerContractFetcher) {
        super(element, renderer);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this._buildApiParams();

        if (this.fetchFromApi) {
            this.apiOptions$.pipe(untilDestroyed(this)).subscribe(options => {
                this.apiOptions = options;

                if (this.setDefault && !this.value) {
                    // If we have been given a list of priorities, use that to set the default
                    let defaultContract: AppSelectOption<CarerContract>;

                    if (this.defaultContractTypePriorities?.length) {
                        // Find the first option that shows up earliest in the priority list
                        for (const priority of this.defaultContractTypePriorities) {
                            defaultContract = options.find(option => option.value.type_id === priority);

                            if (defaultContract) {
                                break;
                            }
                        }
                    }

                    this.writeValue(
                        defaultContract?.value ?? options.flatMap(o => o.value).find(contract => contract.is_default)
                    );
                }
            });
        }
    }

    private _buildApiParams() {
        if (!this.fetchFromApi) {
            return;
        }

        this._apiParams$.next({
            order_by: 'start',
            direction: 'desc',
            ...this._requestedApiParams
        });
    }
}
